import * as signalR from "@microsoft/signalr";
import { appSettings } from "core/config";
import { BusinessPage } from "core/constants";
import { sessionManager } from "core/instances";
import User from "core/models/user";
import AppStore from "core/stores/app.store";
import { updateUserDetailsAsync } from "./get-user-details";

const signalRHelper = {
    baseUrl: appSettings.urls.utilityService,
    dataTimeout: appSettings.signalR.timeout.permissions.data,
    connectionTypes: {
        permissions: "permissions"
    },
    methodTypes: {
        register: "NotifyConnectionRegistration",
        permissionUpdate: "NotifyPermissionUpdate"
    },
    connections: {} as any,

    getConnection: async (connectionType: string): Promise<signalR.HubConnection> => {
        let connection = signalRHelper.connections[connectionType];

        if (!connection) {
            connection = new signalR
                .HubConnectionBuilder()
                .withUrl(`${signalRHelper.baseUrl}/${connectionType}`)
                .withAutomaticReconnect()
                .build();

            try {
                signalRHelper.connections[connectionType] = connection;
                await connection.start({ withCredentials: false });
            }
            catch (e) {
                console.error("Error in hub start:", e);
            }

            const ciamId = User.instance?.company?.ciam;

            connection.on(signalRHelper.methodTypes.register, (registrationSuccess: boolean) => {
                if (registrationSuccess) {
                    console.log(`Connection registered: ${connectionType} - ${ciamId}`);
                }
            });

            connection
                .invoke(signalRHelper.methodTypes.register, ciamId)
                .catch((e: any) => {
                    return console.error(e.toString());
                });
        }

        return connection;
    },

    attachEvent: async (connectionType: string, methodType: string, callback: () => void, description: string) => {
        const connection = await signalRHelper.getConnection(connectionType);

        connection.on(methodType, callback);
        console.log("Event attached", { connectionType, methodType, description });
    },

    removeEvent: (connectionType: string, methodType: string) => {
        const connection = signalRHelper.connections[connectionType];
        if (connection) {
            connection.off(methodType);
            console.log("Event removed", { connectionType, methodType });
        }
    },

    attachPermissionDataRefreshEvent: (store: AppStore) => {

        const callback = () => {
            setTimeout(async () => {
                try {
                    await updateUserDetailsAsync();

                    console.log("Real time permission data refresh");
                }
                catch (e: any) {
                    console.error("Error getting user details - Real time permission update", e);

                    if (e.message.includes("404")) { //User permissions deleted, 404 statusCode contained in message
                        sessionManager.signOut(BusinessPage.dashboard);
                    }
                }
            }, signalRHelper.dataTimeout);
        };

        signalRHelper.attachEvent(signalRHelper.connectionTypes.permissions, signalRHelper.methodTypes.permissionUpdate, callback, "Permission data refresh");
    },

    removePermissionUpdateEvent: () => {
        const connectionType = signalRHelper.connectionTypes.permissions;

        signalRHelper.removeEvent(connectionType, signalRHelper.methodTypes.permissionUpdate);
    },

    notifyPermissionUpdate: async (ciamId: string) => {
        const connection = await signalRHelper.getConnection(signalRHelper.connectionTypes.permissions);

        connection
            .invoke(signalRHelper.methodTypes.permissionUpdate, ciamId)
            .catch((e: any) => {
                return console.error(e.toString());
            });

        console.log(`Real time permission notification: ${ciamId}`);
    }
}

export default signalRHelper;
